<template>
    <div class="cockpit-chart">
        <!-- TÍTULO -->
        <div class="chart-title">
            <router-link
                v-if="linked"
                div="span"
                :to="{ name: 'ven-cockpit-chart', params: { tipo: datacontent.titulo } }"
            >
                {{ title }}
            </router-link>
            <div v-else>{{ title }}</div>
            <div 
                v-if="info != null"
                style="display: inline-block; cursor: pointer; padding-right: 4px"
            >
                <b-icon icon="information" v-tippy :title="getT(info)" />
            </div>
        </div>

        <!-- CONTEÚDO -->
        <div ref="chartContent" class="chart-content" v-if="!noData && !loading">
            <svg ref="svgcontent"></svg>
        </div>

        <!-- FOOTER -->
        <div class="chart-footer" v-if="!noData && !loading">
            <div ref="LGbox" class="legend-chart"></div>
        </div>

        <!-- MSG NÃO HÁ DADOS -->
        <div class="no-data bar" v-if="noData && !loading">
            <svg class="fail">
                <use :xlink:href="require('@/assets/img/icons/chart-error.svg') + '#layer'"></use>
            </svg>
            <div>{{ $t('chart-no-data') }}</div>
        </div>

        <!-- CARREGANDO -->
        <div class="no-data bar" v-if="loading">
            <svg class="loading">
                <use :xlink:href="require('@/assets/img/icons/loader.svg') + '#layer'"></use>
            </svg>
            <div>{{ $t('carregando') }}...</div>
        </div>
    </div>
</template>

<script>
import CockpitChart from '@/components/shared/chart/CockpitChart';
import CockpitVendasService from '@/services/CockpitVendasService';
import CockpitDespesasService from '@/services/CockpitDespesasService';
import CockpitAgroService from '@/services/agro/cockpit/CockpitAgroService';
import { palette as paleta } from '@/functions/color/Palette';

export default {
    extends: CockpitChart,
    props: {
        title: {
            type: String,
            default: '',
        },
        datacontent: {
            type: Object,
            default: () => {
                return {};
            },
        },
        filtro: {
            type: Object,
            default: () => {
                return {};
            },
        },
        linked: {
            type: Boolean,
            default: false,
        },
        module: {
            type: String,
            default: 'VENDAS', // vendas, despesas, promotor
        },
        isPie: {
            type: Boolean,
            default: false,
        },
        info: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dataset: [],
            cores: [],
            filtered: [],
            isAnimating: false,
            service: null,
            loading: true,
            currency: false,
            legends: null,
            tooltipDiv: null,
        };
    },
    created() {
        if (this.module == 'DESPESAS') {
            this.service = new CockpitDespesasService(this.$http, this.$router);
        } else if (this.module == 'AGRO') {
            this.service = new CockpitAgroService(this.$http, this.$router);
        } else {
            this.service = new CockpitVendasService(this.$http, this.$router);
        }

        addEventListener('change-locale', this.handleLanguageChange);
    },
    beforeDestroy() {
        removeEventListener('change-locale', this.handleLanguageChange);
    },
    mounted() {
        this.cores = paleta.getChartColors();

        this.loading = false;

        if (this.noData) {
            this.loadData();
        } else {
            this.mountChart();
        }
    },
    computed: {
        noData() {
            return (
                !this.datacontent || !this.datacontent.items || this.datacontent.items.length == 0
            );
        },
        total() {
            if (this.datacontent && this.datacontent.items) {
                return this.datacontent.items.reduce(
                    (acumulador, atual) => acumulador + atual.value,
                    0
                );
            } else {
                return 0;
            }
        },
    },
    methods: {
        loadData() {
            this.loading = true;
            this.service
                .getChartByTitulo(this.datacontent.titulo, this.filtro)
                .then((r) => {
                    if (r) {
                        this.$emit('update-content', r);
                    } else {
                        console.warn(`Card vazio: ${this.datacontent.titulo}`);
                    }
                })
                .catch((e) => {
                    console.error(`Erro em ${this.datacontent.titulo}`, e);
                })
                .finally((_) => {
                    this.loading = false;
                    this.mountChart();
                });
        },
        mountChart() {
            this.dataset = [...this.datacontent.items];

            if (!this.noData) {
                this.dataset = this.dataset.filter((e) => {
                    return e.value > 0;
                });

                setTimeout(() => {
                    this.drawChartWL();
                }, 10);
            }
        },
        drawChartWL() {
            let data = [
                ...this.datacontent.items.filter((e) => {
                    return e.value > 0;
                }),
            ];
            var d3 = this.$d3;
            var filter = this.filtered;
            var width = +this.$refs.chartContent.offsetWidth;
            var height = +this.$refs.chartContent.offsetHeight;
            height = width < height ? width : height;
            var margin = { top: 20, left: 0, bottom: 20, right: 0 };
            var chartWidth = width - (margin.left + margin.right);
            var chartHeight = height - (margin.top + margin.bottom);

            var color = d3.scaleOrdinal(this.cores);
            this.tooltipDiv = d3
                .select('body')
                .append('div')
                .attr('class', 'chart-tooltip')
                .style('opacity', 0);

            var svg = d3
                .select(this.$refs.svgcontent) // select element in the DOM with id 'chart'
                .append('svg') // append an svg element to the element we've selected
                .attr('width', chartWidth) // set the width of the svg element we just added
                .attr('height', height) // set the height of the svg element we just added
                .append('g') // append 'g' element to the svg element
                .attr(
                    'transform',
                    'translate(' + chartWidth / 2 + ',' + (chartHeight / 2 + 20) + ')'
                ); // our reference is now to the 'g' element. centerting the 'g' element to the svg element

            /**
             * @71172 - feito adaptação para remover o círculo branco do DonutChart quando
             * for PieChart.
             */
            var arc = d3
                .arc()
                .innerRadius(this.isPie ? 0 : chartHeight / 2) // none for pie chart
                .outerRadius(this.isPie ? chartHeight / 2 : chartHeight / 6); // size of overall chart

            var pie = d3
                .pie() // start and end angles of the segments
                .value(function (d) {
                    return d.value;
                }) // how to extract the numerical data from each entry in our dataset
                .sort(null); // by default, data sorts in oescending value. this will mess with our animation so we set it to null

            data.forEach(function (d) {
                d.value = +d.value; // calculate value as we iterate through the data
                d.enabled = true; // add enabled property to track which entries are checked
                d.initvalue = d.value;
            });

            // creating the chart
            var path = svg
                .selectAll('path') // select all path elements inside the svg. specifically the 'g' element. they don't exist yet but they will be created below
                .data(pie(data)) //associate dataset wit he path elements we're about to create. must pass through the pie function. it magically knows how to extract values and bakes it into the pie
                .enter() //creates placeholder nodes for each of the values
                .append('g')
                .attr('class', 'chart-label')
                .append('path') // replace placeholders with path elements
                .attr('d', arc) // define d attribute with arc function above
                .attr('fill', function (d) {
                    return color(d.data.label);
                })
                .attr('stroke', function (d, i) {
                    return color(d.data.label);
                }) // use color scale to define fill of each label in dataset
                .each(function (d) {
                    return this._current - d;
                }); // creates a smooth animation for each track

            path.on('mouseover', this.tooltipMouseoverEvt)
                .on('mousemove', (d) => {
                    this.tooltipDiv
                        .style(
                            'left',
                            +(this.$d3.event.pageX - this.tooltipDiv.node().offsetWidth / 2) + 'px'
                        )
                        .style('top', +(this.$d3.event.pageY - 40) + 'px');
                })
                .on('mouseout', (d) => {
                    this.tooltipDiv.transition().duration(500).style('opacity', 0);
                });

            path.transition('PieChart') // transition of redrawn pie
                .duration(600) //
                .attrTween('d', function (d) {
                    // 'd' specifies the d attribute that we'll be animating
                    var interpolate = d3.interpolate(this._current, d); // this = current path element
                    this._current = interpolate(0); // interpolate between current value and the new value of 'd'
                    return function (t) {
                        let dp = arc(interpolate(t)).split('L');
                        return dp[0].split(/\,/g).length > 2 ? arc(interpolate(t)) : dp[0];
                    };
                });
            let temp = this;
            var pathtext = svg
                .selectAll('.chart-label')
                .append('text')
                .attr('transform', (d) => {
                    var _d = arc.centroid(d);
                    return 'translate(' + _d + ')';
                })
                .attr('dy', '.50em')
                .style('text-anchor', 'middle')
                .style('fill', function (d) {
                    return temp.isColorLight(color(d.data.label)) ? '#000' : '#FFF';
                })
                .style('font-size', (d) => (this.getValue(d) < 6 ? '7px' : '9px'))
                .text((d) => {
                    if (this.getValue(d) < 4) {
                        return '';
                    }
                    return this.getFormattedValue(d);
                });

            let self = this;

            this.legends = this.$d3
                .select(this.$refs.LGbox)
                .attr('font-family', 'sans-serif')
                .attr('font-size', 10)
                .attr('text-anchor', 'end')
                .selectAll('.legend')
                .data(color.domain())
                .enter()
                .append('div')
                .attr('class', 'legend-box')
                .on('click', function (label) {
                    if (!self.isAnimating) {
                        self.isAnimating = true;

                        setTimeout(function () {
                            ff = data.map((d) => {
                                d.value = d.initvalue;
                                return d;
                            });
                            self.isAnimating = false;
                        }, 620);

                        var rect = d3.select(this); // this refers to the colored squared just clicked
                        var enabled = true; // set enabled true to default
                        var totalEnabled = d3.sum(
                            data.map(function (d) {
                                // can't disable all options
                                return d.enabled ? 1 : 0; // return 1 for each enabled entry. and summing it up
                            })
                        );

                        if (rect.attr('class') === 'legend-box disabled') {
                            // if class is disabled
                            rect.attr('class', 'legend-box'); // remove class disabled
                        } else {
                            // else
                            if (totalEnabled < 2) {
                                return; // if less than two labels are flagged, exit
                            }

                            rect.attr('class', 'legend-box disabled'); // otherwise flag the square disabled
                            enabled = false; // set enabled to false
                        }

                        // Update the array to filter the chart by:

                        // add the clicked key if not included:
                        if (filter.indexOf(label) == -1) {
                            filter.push(label);
                            // if all bars are un-checked, reset:
                            if (filter.length == color.domain().length) filter = [];
                        }
                        // otherwise remove it:
                        else {
                            filter.splice(filter.indexOf(label), 1);
                        }

                        pie.value(function (d) {
                            if (d.label === label) d.enabled = enabled; // if entry label matches legend label
                            return d.enabled ? d.value : 0; // update enabled property and return value or 0 based on the entry's status
                        });

                        path = path.data(pie(data)); // update pie with new data

                        path.transition() // transition of redrawn pie
                            .duration(600) //
                            .attrTween('d', function (d, i) {
                                // 'd' specifies the d attribute that we'll be animating
                                var interpolate = d3.interpolate(this._current, d); // this = current path element
                                this._current = interpolate(0); // interpolate between current value and the new value of 'd'
                                return function (t) {
                                    let dp = arc(interpolate(t)).split('L');
                                    return dp[0].split(/\,/g).length > 2
                                        ? arc(interpolate(t))
                                        : dp[0];
                                };
                            });

                        let ff = data.map((d) => {
                            if (!d.enabled) {
                                d.value = 0;
                            } else {
                                d.value = d.initvalue;
                            }
                            return d;
                        });
                        let total = 0;
                        data.forEach((f) => {
                            if (f.enabled) {
                                total += f.value;
                            }
                        });
                        pathtext
                            .data(pie(ff))
                            .transition() // transition of redrawn pie
                            .duration(600) //
                            .attrTween('transform', function (d) {
                                var interpolate = d3.interpolate(this._current, d);
                                var _this = this;
                                return function (t) {
                                    var _d = arc.centroid(d);
                                    _d[0] *= 1.0; //multiply by a constant factor
                                    _d[1] *= 1.0; //multiply by a constant factor
                                    return 'translate(' + _d + ')';
                                };
                            })
                            .attr('text-anchor', 'middle')
                            .style('opacity', function (d) {
                                if (!d.data.enabled) {
                                    return 0;
                                }
                                return 1;
                            })
                            .text((d) => {
                                if (self.getValue(d, total) < 4) {
                                    return '';
                                }
                                return self.getFormattedValue(d, total);
                            });

                        // update legend:
                        self.legends.style('opacity', function (d) {
                            if (filter.length) {
                                if (filter.indexOf(d) == -1) {
                                    return 1;
                                } else {
                                    return 0.4;
                                }
                            } else {
                                return 1;
                            }
                        });
                    }
                });

            this.legends
                .append('div')
                .style('width', this.legendRectSize + 'px')
                .style('height', this.legendRectSize + 'px')
                .attr('class', 'legend-color-box')
                .style('background', color);

            this.legends
                .append('p')
                .style('text-transform', 'uppercase')
                .attr('class', this.handleLegendClass)
                .text(this.handleLegendRender);
        },
        getValue(d, total = this.total) {
            let percent = (d.data.value * 100) / total;
            return percent;
        },
        getFormattedValue(d, total) {
            return (Math.floor(this.getValue(d, total) * 100) / 100 + ' %').replace('.', ',');
        },
        midAngle(d) {
            return d.startAngle + (d.endAngle - d.startAngle) / 2;
        },
        redrawChart() {
            if (this.datacontent.items.length > 0) {
                this.$d3.select(this.$refs.svgcontent).selectAll('g').remove();
                this.$d3.select(this.$refs.LGbox).selectAll('.legend-box').remove();
                setTimeout(this.drawChartWL, 50);
            }
        },
        handleLegendRender(label) {
            if (['sem-regiao', 'sem-atividade'].includes(label)) {
                return this.$t(label);
            } else {
                return label;
            }
        },
        handleLegendClass(label) {
            if (['sem-regiao', 'sem-atividade'].includes(label)) {
                return 'dic-legend';
            } else {
                return '';
            }
        },
        handleLanguageChange() {
            this.legends.select('.dic-legend').text((label) => {
                return this.$t(label);
            });
        },
        tooltipMouseoverEvt(d) {
            let label = '';

            if (['sem-regiao', 'sem-atividade'].includes(d.data.label)) {
                label = this.$t(d.data.label);
            } else {
                label = d.data.label.split('-')[0];
            }

            let temp = this.isCurrencyChart(this.datacontent.titulo) 
                ? this.getDecimal(d.data.value, null, true)
                : this.getDecimal(d.data.value, 0, false)

            let msg = `<b>${label}:</b> ${temp}`;

            this.tooltipDiv
                .transition()
                .duration(200)
                .style('opacity', this.legendOpacity)
                .style('color', '#FFF');

            let left = +this.$d3.event.pageX - this.tooltipDiv.node().offsetWidth / 2;
            let top = +this.$d3.event.pageY - 40;
            this.tooltipDiv
                .html(msg)
                .style('left', left + 'px')
                .style('top', top + 'px');
        },
    },
};
</script>
